import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getRateTypeDiscounts,
  getRateTypeDiscount,
  createRateTypeDiscount,
  updateRateTypeDiscount,
  deleteRateTypeDiscount,
} from './rate-type-discounts.service';
import {
  TCreateRateTypeDiscount,
  TUpdateRateTypeDiscount,
} from '~/types/rate-type-discount';

export const RATE_TYPE_DISCOUNT_KEYS = {
  all: ['rate-type-discounts'] as const,
  lists: () => [...RATE_TYPE_DISCOUNT_KEYS.all, 'list'] as const,
  details: () => [...RATE_TYPE_DISCOUNT_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...RATE_TYPE_DISCOUNT_KEYS.details(), id] as const,
};

export const useGetRateTypeDiscounts = () => {
  return useQuery({
    queryKey: RATE_TYPE_DISCOUNT_KEYS.lists(),
    queryFn: () => getRateTypeDiscounts(),
  });
};

export const useGetRateTypeDiscount = (id: string | undefined) => {
  return useQuery({
    queryKey: RATE_TYPE_DISCOUNT_KEYS.detail(id!),
    queryFn: () => getRateTypeDiscount(id!),
    enabled: !!id,
  });
};

export const useCreateRateTypeDiscount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TCreateRateTypeDiscount) => createRateTypeDiscount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RATE_TYPE_DISCOUNT_KEYS.lists() });
    },
  });
};

export const useUpdateRateTypeDiscount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TUpdateRateTypeDiscount }) =>
      updateRateTypeDiscount(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: RATE_TYPE_DISCOUNT_KEYS.lists() });
      queryClient.invalidateQueries({
        queryKey: RATE_TYPE_DISCOUNT_KEYS.detail(variables.id),
      });
    },
  });
};

export const useDeleteRateTypeDiscount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteRateTypeDiscount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RATE_TYPE_DISCOUNT_KEYS.lists() });
    },
  });
};
